import { useEffect, useState } from 'react'

import {
  useMutationUpdateBooking,
  useQueryGetAllBookingProducts,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type ParameterID } from '@flecto/types'
import { useNotifications } from '@flecto/ui'

export type UseBookingTaxesToggleOptions = {
  bookingId: ParameterID
}

export function useBookingTaxesToggle({
  bookingId,
}: UseBookingTaxesToggleOptions) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking' })
  const { showNotification } = useNotifications()
  const { mutateAsync: updateBooking, isLoading } = useMutationUpdateBooking()
  const { data: booking } = useQueryGetBookingById({ bookingId })
  const { data: bookingProducts } = useQueryGetAllBookingProducts({
    bookingId,
  })
  const [isChecked, setIsChecked] = useState(false)

  useEffect(() => {
    const foundItem = bookingProducts?.find((item) => item.taxable)
    const foundService = booking?.booking_services?.find((serv) => serv.taxable)

    setIsChecked(Boolean(foundItem) || Boolean(foundService))
  }, [booking])

  const toggleTaxes = async (value: boolean) => {
    const toggleType = value ? 'on' : 'off'

    try {
      const itemsToUpdate = bookingProducts?.map((item) => ({
        id: item.id,
        taxable: value,
      }))

      const servicesToUpdate = booking?.booking_services?.map((service) => ({
        id: service.id,
        taxable: value,
      }))

      if (bookingId) {
        await updateBooking({
          bookingId,
          booking: {
            booking_items_attributes: itemsToUpdate,
            booking_services_attributes: servicesToUpdate,
          },
        })
      }
      showNotification({
        type: 'success',
        message: t(`notifications.taxes_toggle_${toggleType}_success`),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t(`notifications.taxes_toggle_${toggleType}_error`),
      })
    }
  }

  return {
    toggleTaxes,
    isLoading,
    isChecked,
  }
}
